import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import CountUp from "react-countup";

import { InferenceParameters, ObjectType } from "@/types";

import { Loader2D } from "./ProgressLoaders/Loader2D";
import { Loader3D } from "./ProgressLoaders/Loader3D";
import { LoaderGeneric } from "./ProgressLoaders/LoaderGeneric";
import { LoaderUpscale } from "./ProgressLoaders/LoaderUpscale";
import { LoaderVideo } from "./ProgressLoaders/LoaderVideo";

interface InProgressContainerProps {
  darkened?: boolean;
}

const InProgressContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "darkened",
})<InProgressContainerProps>(({ theme, darkened }) => ({
  width: "100%",
  aspectRatio: "1/1",
  position: "relative",
  border: `1px solid ${theme.palette.secondary[8]}`,
  borderRadius: "4px",
  padding: theme.spacing(0.5),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: darkened
    ? theme.palette.secondary[7]
    : theme.palette.secondary[6],
}));

interface LoadingTextProps {
  message: string;
}

interface TextContainerProps {
  cardSize: number;
}

const TextContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "cardSize",
})<TextContainerProps>(({ cardSize, theme }) => ({
  "--cardSize": `${cardSize / 15}px`,

  width: "100%",
  maxWidth: "180px",
  paddingLeft: "8px",
  paddingRight: "8px",
  display: "flex",
  alignItems: "center",
  marginTop: "var(--cardSize)",
  gap: theme.spacing(1),
}));

const AnimatedTypography = styled(Typography)(() => ({
  whiteSpace: "nowrap",

  "::after": {
    content: '"..."',
    position: "absolute",
    display: "inline-block",
    animation: "ellipsis 1.5s steps(4, end) infinite",
  },

  "@keyframes ellipsis": {
    "0%": {
      content: '""',
    },
    "25%": {
      content: '"."',
    },
    "50%": {
      content: '".."',
    },
    "75%": {
      content: '"..."',
    },
    "100%": {
      content: '""',
    },
  },
}));

const LoadingText: React.FC<LoadingTextProps> = ({ message }) => {
  return (
    <AnimatedTypography color="secondary.1" fontWeight="500" variant="small">
      {message}
    </AnimatedTypography>
  );
};

type FileImageInProgressProps = {
  objectType: ObjectType;
  inferenceParameters?: InferenceParameters;
  startedAt: Date;
  etaAt: Date;
  url?: string;
  cardSize?: number;
  darkened?: boolean;
};

export const FileImageInProgress = ({
  objectType,
  inferenceParameters,
  startedAt,
  etaAt,
  url,
  cardSize = 120,
  darkened,
}: FileImageInProgressProps) => {
  const now = new Date();
  const startCount = Math.max((now.getTime() - startedAt.getTime()) / 1000, 0);
  const endCount = Math.max((etaAt.getTime(), startedAt.getTime()) / 1000, 0);
  const smallCardSize = cardSize <= 130;

  const [hasCountEnded, setHasCountEnded] = useState(startCount >= endCount);

  let message = "Forging";
  if (objectType === "image") {
    message = "Forging Image";
  } else if (objectType === "video") {
    message = "Making It Move";
  } else if (objectType === "mesh") {
    message = "Forging 3D";
  } else if (objectType === "audio") {
    message = "Forging Audio";
  }
  // override message for specific parameters
  if (inferenceParameters?.upscaleRatio) {
    message = "Upscaling";
  } else if (inferenceParameters?.vectorize) {
    message = "Vectorizing";
  } else if (inferenceParameters?.removeBackground) {
    message = "Removing BG";
  } else if (inferenceParameters?.reframe) {
    message = "Reframing";
  } else if (inferenceParameters?.addSfxToVideo) {
    message = "Adding SFX";
  }
  if (hasCountEnded) {
    message = smallCardSize ? "Polishing" : "Polishing the pixels";
  }

  const renderLoader = () => {
    if (inferenceParameters?.upscaleRatio) {
      return <LoaderUpscale backgroundImage={url} cardSize={cardSize} />;
    }
    switch (objectType) {
      case "video":
        return <LoaderVideo cardSize={cardSize} />;
      case "mesh":
        return <Loader3D cardSize={cardSize} />;
      case "image":
        return <Loader2D cardSize={cardSize} />;
      default:
        return <LoaderGeneric cardSize={cardSize} />;
    }
  };

  return (
    <InProgressContainer darkened={darkened}>
      {renderLoader()}

      <TextContainer
        cardSize={cardSize}
        justifyContent={hasCountEnded ? "center" : "space-between"}
        flexDirection={smallCardSize ? "column" : "row"}
      >
        <LoadingText message={message} />
        {!hasCountEnded && (
          <Typography
            color="secondary.2"
            variant="regular"
            whiteSpace="nowrap"
            fontSize={smallCardSize ? 12 : 14}
            marginBottom={smallCardSize ? -3 : 0}
          >
            <CountUp
              start={startCount}
              end={endCount}
              duration={endCount - startCount}
              useEasing={false}
              suffix="s"
              decimals={2}
              onEnd={() => setHasCountEnded(true)}
            />
          </Typography>
        )}
      </TextContainer>
    </InProgressContainer>
  );
};
